// src/components/MileageDetailModal.tsx - NEW COMPONENT

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Paper,
  Stack,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FileDownload as DownloadIcon,
  DirectionsCar as CarIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

interface Vehicle {
  imei: string;
  plateNumber: string;
  make?: string;
  model?: string;
  mileage: number;
  displayName?: string;
}

interface MileageDetailModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  data: {
    totalMileage: number;
    vehicleCount: number;
    avgMileagePerVehicle: number;
    vehicles: Vehicle[];
  } | null;
}

type SortField = 'plateNumber' | 'mileage';
type SortOrder = 'asc' | 'desc';

const MileageDetailModal: React.FC<MileageDetailModalProps> = ({
  open,
  onClose,
  date,
  data,
}) => {
  const theme = useTheme();
  const [sortField, setSortField] = useState<SortField>('mileage');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and sort vehicles
  const filteredAndSortedVehicles = useMemo(() => {
    if (!data || !data.vehicles) return [];

    let filtered = data.vehicles.filter((vehicle) =>
      vehicle.plateNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.make?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle string comparison for plateNumber
      if (sortField === 'plateNumber') {
        aVal = aVal as string;
        bVal = bVal as string;
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [data, searchQuery, sortField, sortOrder]);

  if (!data) return null;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Vehicle', 'Make/Model', 'Distance (km)', 'Percentage of Fleet Total'];
    const rows = filteredAndSortedVehicles.map(v => [
      v.plateNumber,
      `${v.make || ''} ${v.model || ''}`.trim(),
      v.mileage.toFixed(2),
      `${((v.mileage / data.totalMileage) * 100).toFixed(1)}%`,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleet-mileage-${date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getMileageColor = (mileage: number) => {
    const avg = data.avgMileagePerVehicle;
    if (mileage > avg * 1.5) return theme.palette.error.main;
    if (mileage > avg) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Fleet Mileage Details
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {new Date(date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Summary Cards */}
      <Box sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
              bgcolor: alpha(theme.palette.info.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TimelineIcon sx={{ color: 'info.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Fleet Mileage
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {data.totalMileage.toLocaleString()} km
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SpeedIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Average per Vehicle
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {data.avgMileagePerVehicle.toFixed(0)} km
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
              bgcolor: alpha(theme.palette.success.main, 0.05),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CarIcon sx={{ color: 'success.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Vehicles Active
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {data.vehicleCount}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Search and Export */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by vehicle plate, make, or model..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Export to CSV">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Export
            </Button>
          </Tooltip>
        </Box>

        {/* Results Count */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredAndSortedVehicles.length} of {data.vehicles.length} vehicles
        </Typography>

        {/* Table */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 400, overflow: 'auto' }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'plateNumber'}
                    direction={sortField === 'plateNumber' ? sortOrder : 'asc'}
                    onClick={() => handleSort('plateNumber')}
                  >
                    Vehicle
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  Make / Model
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  <TableSortLabel
                    active={sortField === 'mileage'}
                    direction={sortField === 'mileage' ? sortOrder : 'asc'}
                    onClick={() => handleSort('mileage')}
                  >
                    Distance (km)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  % of Fleet Total
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No vehicles found matching "{searchQuery}"
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedVehicles.map((vehicle, index) => {
                  const percentage = (vehicle.mileage / data.totalMileage) * 100;
                  return (
                    <TableRow
                      key={vehicle.imei}
                      sx={{
                        '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.05) },
                        bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {vehicle.plateNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {vehicle.make} {vehicle.model}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${vehicle.mileage.toFixed(2)} km`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(getMileageColor(vehicle.mileage), 0.1),
                            color: getMileageColor(vehicle.mileage),
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          {percentage.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {vehicle.mileage > data.avgMileagePerVehicle * 1.5 ? (
                          <Chip
                            label="High Usage"
                            size="small"
                            color="error"
                            sx={{ minWidth: 90 }}
                          />
                        ) : vehicle.mileage > data.avgMileagePerVehicle ? (
                          <Chip
                            label="Above Avg"
                            size="small"
                            color="warning"
                            sx={{ minWidth: 90 }}
                          />
                        ) : (
                          <Chip
                            label="Normal"
                            size="small"
                            color="success"
                            sx={{ minWidth: 90 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MileageDetailModal;